/**
 * Hook for managing transcription and summary languages.
 *
 * Fetches the available languages from the ASR service. The selected languages
 * are user preferences: in the main app they persist server-side (via the
 * settings `features`, so they follow the user across devices); in the
 * tokenless embed there is no scribe session, so selection stays local.
 */

import { useSettings } from '@/hooks/useSettings';
import { LANGUAGE_CACHE_SESSION_KEY } from '@/lib/orgScopedState';
import { DEFAULT_LANGUAGES, LANGUAGE_NAME_MAP, type Language } from '@/types/session';
import { useEffect, useState } from 'react';

const STORAGE_KEY = 'selectedTranscriptionLanguage';
const SUMMARY_STORAGE_KEY = 'selectedSummaryLanguage';
const DEFAULT_LANGUAGE = 'is';

// The session-scoped cache below holds the last server-confirmed languages so
// a cold reload doesn't fall back to Icelandic during the settings fetch (a
// recording started in that window would transcribe in the wrong language).
// Org-scoped: cleared on org switch via ORG_SCOPED_SESSION_KEYS.

interface CachedLanguages {
  transcription?: string | null;
  summary?: string | null;
}

const readLanguageCache = (): CachedLanguages => {
  try {
    return JSON.parse(sessionStorage.getItem(LANGUAGE_CACHE_SESSION_KEY) ?? '{}');
  } catch {
    return {};
  }
};

type LanguagePersistMode = 'server' | 'local';

interface UseLanguagesOptions {
  /** 'server' persists via settings features; 'local' keeps localStorage (embed). */
  persist?: LanguagePersistMode;
}

interface LanguageSelection {
  languages: Language[];
  selectedLanguage: string;
  setSelectedLanguage: (language: string) => void;
  selectedSummaryLanguage: string;
  setSelectedSummaryLanguage: (language: string) => void;
}

// Guard so the one-time localStorage → server migration runs at most once per
// app session, no matter how many components mount the hook.
let languageMigrationAttempted = false;

const readLegacy = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const clearLegacy = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // localStorage unavailable — nothing to migrate away.
  }
};

export function useLanguages({ persist = 'server' }: UseLanguagesOptions = {}): LanguageSelection {
  const isServer = persist === 'server';

  // Available languages from the ASR service (both modes).
  const [languages, setLanguages] = useState<Language[]>(DEFAULT_LANGUAGES);
  useEffect(() => {
    async function fetchLanguages() {
      try {
        const response = await fetch('/api/asr/languages', { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          if (data.languages && data.languages.length > 0) {
            // Filter out multilang and use Icelandic names
            const filtered = data.languages
              .filter((l: { code: string }) => l.code !== 'multilang')
              .map((l: { code: string; name: string }) => ({
                code: l.code,
                name: LANGUAGE_NAME_MAP[l.code] || l.name,
              }));
            setLanguages(filtered);
          }
        }
      } catch (error) {
        console.error('Failed to fetch languages:', error);
      }
    }
    fetchLanguages();
  }, []);

  // Local (embed) selection state, persisted to localStorage.
  const [localLanguage, setLocalLanguage] = useState<string>(() => readLegacy(STORAGE_KEY) || DEFAULT_LANGUAGE);
  const [localSummaryLanguage, setLocalSummaryLanguage] = useState<string>(
    () => readLegacy(SUMMARY_STORAGE_KEY) || readLegacy(STORAGE_KEY) || DEFAULT_LANGUAGE
  );

  useEffect(() => {
    if (!isServer && localLanguage) localStorage.setItem(STORAGE_KEY, localLanguage);
  }, [isServer, localLanguage]);
  useEffect(() => {
    if (!isServer && localSummaryLanguage) localStorage.setItem(SUMMARY_STORAGE_KEY, localSummaryLanguage);
  }, [isServer, localSummaryLanguage]);

  // Server (main app) selection via settings features.
  const settings = useSettings({ enabled: isServer });

  // One-time migration: push any legacy localStorage language the server
  // doesn't already have, then drop the local copies. Both keys go in a single
  // write so the two never race and clobber each other's features. Gated on a
  // SUCCESSFUL settings load (not just "not loading") so a failed GET can't
  // make a null server value look authoritative; the legacy keys are only
  // cleared once the server write is confirmed, so a failed PUT keeps the
  // migration source for the next session.
  // biome-ignore lint/correctness/useExhaustiveDependencies: one-shot migration; settings.setFeatures is stable enough
  useEffect(() => {
    if (!isServer || languageMigrationAttempted || !settings.isReady) return;
    languageMigrationAttempted = true;

    const legacyTranscription = readLegacy(STORAGE_KEY);
    const legacySummary = readLegacy(SUMMARY_STORAGE_KEY);
    const updates: Record<string, string> = {};
    if (settings.transcriptionLanguage == null && legacyTranscription) {
      updates.transcription_language = legacyTranscription;
    }
    if (settings.summaryLanguage == null && legacySummary) {
      updates.summary_language = legacySummary;
    }
    const clearBoth = () => {
      clearLegacy(STORAGE_KEY);
      clearLegacy(SUMMARY_STORAGE_KEY);
    };
    if (Object.keys(updates).length > 0) {
      settings.setFeatures(updates, { onSuccess: clearBoth });
    } else {
      clearBoth();
    }
  }, [isServer, settings.isReady, settings.transcriptionLanguage, settings.summaryLanguage]);

  // Keep the per-tab cache in sync with the server-confirmed values.
  useEffect(() => {
    if (!isServer || !settings.isReady) return;
    try {
      sessionStorage.setItem(
        LANGUAGE_CACHE_SESSION_KEY,
        JSON.stringify({ transcription: settings.transcriptionLanguage, summary: settings.summaryLanguage })
      );
    } catch {
      // sessionStorage unavailable — the cache is a best-effort convenience.
    }
  }, [isServer, settings.isReady, settings.transcriptionLanguage, settings.summaryLanguage]);

  if (isServer) {
    // While settings load, fall back to the cached last-known values so a
    // recording started immediately after a reload uses the right language.
    const cached = settings.isReady ? {} : readLanguageCache();
    const transcription = settings.transcriptionLanguage ?? cached.transcription ?? null;
    const summary = settings.summaryLanguage ?? cached.summary ?? null;
    return {
      languages,
      selectedLanguage: transcription ?? DEFAULT_LANGUAGE,
      setSelectedLanguage: settings.setTranscriptionLanguage,
      selectedSummaryLanguage: summary ?? transcription ?? DEFAULT_LANGUAGE,
      setSelectedSummaryLanguage: settings.setSummaryLanguage,
    };
  }

  return {
    languages,
    selectedLanguage: localLanguage,
    setSelectedLanguage: setLocalLanguage,
    selectedSummaryLanguage: localSummaryLanguage,
    setSelectedSummaryLanguage: setLocalSummaryLanguage,
  };
}
